import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef, MatDialogModule} from '@angular/material/dialog';
import {MatDividerModule} from '@angular/material/divider';
import {NgStyle, AsyncPipe} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {TranslateModule} from '@ngx-translate/core';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import * as convert from 'color-convert';
import {
  DialogCloseButtonComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent
} from '@q9elements/ui-kit/components';
import {SfaHeaderService} from '../../../diagram-render/sfa-header/sfa-header.service';
import {DraggableDialogDirective} from '../../shared/directives/draggable-dialog.directive';

@Component({
  selector: 'app-header-color-picker',
  templateUrl: './header-color-picker.component.html',
  styleUrls: ['./header-color-picker.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    DraggableDialogDirective,
    MatButtonModule,
    MatIconModule,
    NgStyle,
    AsyncPipe,
    TranslateModule,
    DialogCloseButtonComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    MatDividerModule
  ]
})
export class HeaderColorPickerComponent implements OnInit, OnDestroy {
  public readonly defaultColor = '#e5e5e5';

  private readonly lightFont = '#ffffff';
  private readonly darkFont = '#000000';

  // for calculations of font color based on background color
  private readonly redMultiplier = 0.299;
  private readonly greenMultiplier = 0.587;
  private readonly blueMultiplier = 0.114;
  private readonly colorSwitchThreshold = 150; // optimal values are between 150 and 186

  public currentSelectedColor = this.defaultColor;
  public previousColor = this.defaultColor;

  public fontColor$: Observable<string> = this.sfaHeaderSvc.previewColor$.pipe(
    map(bgColor => this.getCurrentFontColor(bgColor))
  );
  public previousFontColor;
  public defaultFontColor;

  private lastPreviewed: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<HeaderColorPickerComponent>,
    public sfaHeaderSvc: SfaHeaderService
  ) {}

  public ngOnInit() {
    this.previousColor = this.data.previousColor || this.defaultColor;
    this.currentSelectedColor = this.previousColor;
    this.previousFontColor = this.getCurrentFontColor(this.previousColor);
    this.defaultFontColor = this.getCurrentFontColor(this.defaultColor);
  }

  public ngOnDestroy() {
    this.previewColor(null);
  }

  public openPalette(event) {
    event.click();
  }

  public colorSelected(event): void {
    this.previewColor(event.target.value);
    this.lastPreviewed = event.target.value;
  }

  public finishedSelecting(event): void {
    this.previewColor(event.target.value);
    this.currentSelectedColor = event.target.value;
    this.lastPreviewed = null;
  }

  public previewDefaultColor(): void {
    this.previewColor(this.defaultColor);
  }

  public previewPreviousColor(): void {
    this.previewColor(this.previousColor);
  }

  public cancelPreview(): void {
    this.previewColor(this.lastPreviewed || this.currentSelectedColor);
  }

  public selectFromOptions(color: string): void {
    this.currentSelectedColor = color;
  }

  public applyColor(): void {
    this.dialogRef.close(this.currentSelectedColor);
  }

  private previewColor(color: string): void {
    this.sfaHeaderSvc.nextPreviewColor(color);
  }

  private getCurrentFontColor(color: string): string {
    const [red, green, blue] = convert.hex.rgb(color || this.currentSelectedColor);
    const convertValue =
      red * this.redMultiplier + green * this.greenMultiplier + blue * this.blueMultiplier;

    return convertValue > this.colorSwitchThreshold ? this.darkFont : this.lightFont;
  }
}
